/**
 * @ipc/admin-ui · entities/tenant · model/hooks — TanStack Query хуки тенантов.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { queryKeys, ApiError } from '@/shared/api';
import {
  fetchTenants,
  fetchTenant,
  updateTenant,
  createTenant,
  fetchTenantAnalyticsSummary,
  fetchTenantAnalyticsTimeseries,
  fetchTenantAnalyticsBreakdown,
  fetchTenantAdmins,
  createTenantAdmin,
  updateTenantAdmin,
  fetchTenantRoles,
  type TenantAnalyticsFilters,
  type TenantAdminsPage,
} from '../api/tenant-api';
import type { Tenant, UpdateTenantInput, CreateTenantInput, CreateTenantResult } from './types';
import type { SummaryResponse, TimeseriesResponse, BreakdownResponse } from '@/entities/analytics';
import type { AdminUser, RoleOption, CreateAdminUserDto, UpdateAdminUserDto } from '@/entities/admin-user';

export function useTenantsQuery(): UseQueryResult<Tenant[], ApiError> {
  return useQuery<Tenant[], ApiError>({
    queryKey: queryKeys.tenants.list(),
    queryFn: () => fetchTenants(),
    staleTime: 60_000,
  });
}

export function useTenantQuery(slug: string | undefined): UseQueryResult<Tenant, ApiError> {
  return useQuery<Tenant, ApiError>({
    queryKey: queryKeys.tenants.detail(slug ?? ''),
    queryFn: () => fetchTenant(slug as string),
    enabled: slug !== undefined && slug.length > 0,
  });
}

export function useUpdateTenantMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateTenantInput) => updateTenant(slug, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.tenants.all() });
    },
  });
}

export function useCreateTenantMutation() {
  const qc = useQueryClient();
  return useMutation<CreateTenantResult, ApiError, CreateTenantInput>({
    mutationFn: (body: CreateTenantInput) => createTenant(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.tenants.all() });
    },
  });
}

function filtersReady(filters: TenantAnalyticsFilters): boolean {
  return filters.preset !== 'custom' || (Boolean(filters.from) && Boolean(filters.to));
}

function analyticsFiltersAsParams(
  filters: TenantAnalyticsFilters,
): Record<string, string | number | boolean | null | undefined> {
  return {
    preset: filters.preset,
    from: filters.from,
    to: filters.to,
    merchantId: filters.merchantId,
    provider: filters.provider,
    status: filters.status,
    ps: filters.ps,
  };
}

export function useTenantAnalyticsSummaryQuery(
  slug: string | undefined,
  filters: TenantAnalyticsFilters,
): UseQueryResult<SummaryResponse, ApiError> {
  return useQuery({
    queryKey: queryKeys.tenants.analyticsSummary(slug ?? '', analyticsFiltersAsParams(filters)),
    queryFn: () => fetchTenantAnalyticsSummary(slug as string, filters),
    enabled: Boolean(slug) && filtersReady(filters),
    staleTime: 30_000,
  });
}

export function useTenantAnalyticsTimeseriesQuery(
  slug: string | undefined,
  filters: TenantAnalyticsFilters,
): UseQueryResult<TimeseriesResponse, ApiError> {
  return useQuery({
    queryKey: queryKeys.tenants.analyticsTimeseries(slug ?? '', analyticsFiltersAsParams(filters)),
    queryFn: () => fetchTenantAnalyticsTimeseries(slug as string, filters),
    enabled: Boolean(slug) && filtersReady(filters),
    staleTime: 30_000,
  });
}

export function useTenantAnalyticsBreakdownQuery(
  slug: string | undefined,
  filters: TenantAnalyticsFilters & { groupBy: string },
): UseQueryResult<BreakdownResponse, ApiError> {
  return useQuery({
    queryKey: queryKeys.tenants.analyticsBreakdown(
      slug ?? '',
      filters.groupBy,
      analyticsFiltersAsParams(filters),
    ),
    queryFn: () => fetchTenantAnalyticsBreakdown(slug as string, filters),
    enabled: Boolean(slug) && filtersReady(filters),
    staleTime: 30_000,
  });
}

export function useTenantAdminsQuery(
  slug: string | undefined,
  filters: { page: number; limit: number; search?: string },
): UseQueryResult<TenantAdminsPage, ApiError> {
  return useQuery({
    queryKey: queryKeys.tenants.admins(slug ?? '', {
      page: filters.page,
      limit: filters.limit,
      search: filters.search ?? null,
    }),
    queryFn: () => fetchTenantAdmins(slug as string, filters),
    enabled: Boolean(slug),
  });
}

export function useTenantRolesQuery(slug: string | undefined): UseQueryResult<RoleOption[], ApiError> {
  return useQuery({
    queryKey: queryKeys.tenants.roles(slug ?? ''),
    queryFn: () => fetchTenantRoles(slug as string),
    enabled: Boolean(slug),
    staleTime: 60_000,
  });
}

export function useCreateTenantAdminMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAdminUserDto) => createTenantAdmin(slug, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tenants', 'admins', slug] });
    },
  });
}

export function useUpdateTenantAdminMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateAdminUserDto }) =>
      updateTenantAdmin(slug, id, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tenants', 'admins', slug] });
    },
  });
}
